import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";

import { useProject } from "../app/projectContext";
import { useStudyDraft } from "../hooks/useStudyDraft";
import { earthingRunCreateRequestSchema, type EarthingRunCreateRequest } from "../services/earthing";
import {
  conductorMaterialSchema,
  conductorRoleSchema,
  electrodeKindSchema,
  kTableIdSchema,
  systemKindSchema,
} from "../services/earthingContract";
import type { FaultRunFaultLevel } from "../services/faultLink";
import type { SldElements } from "../services/sldElements";
import { describeValidationIssue, type ValidationIssueLike } from "../utils/validationMessages";
import { DisconnectionRowSelector } from "./DisconnectionRowSelector";
import { FaultRunSelector } from "./FaultRunSelector";
import { OtherRevisionDraftNotice } from "./OtherRevisionDraftNotice";
import { SldElementPicker } from "./SldElementPicker";
import {
  buildEarthingRunPayload,
  columnsOf,
  createConductorRowDraft,
  createElectrodeDraft,
  createInitialEarthingDraft,
  EARTHING_DRAFT_MODULE,
  EARTHING_LABELS,
  earthingDraftSchema,
  ELECTRODE_DIMENSION_FIELDS,
  ELECTRODE_KIND_LABELS,
  isBoardRole,
  K_COLUMN_LABELS,
  K_SOURCE_LABELS,
  K_SOURCES,
  K_TABLE_LABELS,
  LINK_SOURCE_LABELS,
  LINK_SOURCES,
  MATERIAL_LABELS,
  ROLE_LABELS,
  seedEarthingIdCounters,
  SYSTEM_KIND_LABELS,
  type ConductorRowDraft,
  type EarthingDraft,
  type ElectrodeDraft,
  type KSourceChoice,
  type LinkSourceChoice,
} from "./earthingDraft";
import { ELECTRODE_DIMENSIONS } from "../services/earthingContract";

/** The first option of every choice: nothing is chosen for the engineer. */
const CHOOSE = "Choose…";

export { NO_FAULT_RUNS_NOTE, NO_OPEN_REVISION_NOTE, faultRunOptionText } from "./FaultRunSelector";
export { DISCONNECTION_ROW_HINT, NO_EARTH_FAULT_LOOP_RUNS_NOTE } from "./DisconnectionRowSelector";

export const NOT_MAXIMUM_EARTH_FAULT_NOTE =
  "This run is not the maximum-case single-phase-to-earth fault; the bus earth-fault current " +
  "is read from it as stored and the result will be Engineering review required.";

/** "The fault run bus <x> differs from the arrangement's bus <y>; ..." */
export function busMismatchNote(faultBus: string, studyBus: string): string {
  return (
    `The fault run bus ${faultBus} differs from the arrangement's bus ${studyBus}; the result ` +
    "will be Engineering review required."
  );
}

/** The run a board conductor wants: the maximum-case single-phase-to-earth fault (D3). */
export function isMaximumEarthFault(level: FaultRunFaultLevel): boolean {
  return level.calculation_case === "MAXIMUM" && level.fault_type === "SINGLE_PHASE_TO_EARTH";
}

export const CONDUCTOR_HINT =
  "A circuit protective conductor takes t, I and S from one row of a stored earth-fault loop " +
  "run; a conductor at the board (the main protective conductor, the earthing conductor) " +
  "takes the bus earth-fault current from a stored maximum single-phase-to-earth fault run. A " +
  "declared figure governs over a linked one. The required area is the adiabatic formula of " +
  "IS 3043:2018 clause 17.2.2.1 as printed, S = √(I²t) / k, compared with the declared area; " +
  "no standard size is selected.";

export const K_HINT =
  "k from a held table is the value printed in IS 3043:2018 Table 11A to 11D or CPWD Part I " +
  "2023 App F Table F-6 / F-7 for the conductor's material and the named column, with its " +
  "temperature limits; a declared k is taken as typed with its source and marked not verified.";

export const ELECTRODE_HINT =
  "Each electrode's resistance is the printed formula of IS 3043:2018 clause 14.2 for its kind " +
  "in a uniform soil at the design resistivity; a group in parallel is divided by the count " +
  "times the declared utilisation factor (the spacing is not checked); the groups are combined " +
  "in ideal parallel. Minimum dimensions, corrosion allowance and current loading are not " +
  "checked.";

export const SOIL_HINT =
  "The measured resistivity with its test record; a declared seasonal factor multiplies it " +
  "into the design resistivity. Without a resistivity no electrode resistance is formed.";

export const PROFILE_NOTE =
  "The IN profile holds the IS 3043 and CPWD k tables as controlled engineering data; every " +
  "other figure is the engineer's declared value with its source or a stored run's. The " +
  "profile is recorded with the run and must match the project revision.";

type EarthingFormProps = {
  disabled?: boolean;
  onSubmit: (payload: EarthingRunCreateRequest) => void | Promise<void>;
  // The revision's single-line diagram, when it has one (the page reads it): the arrangement's
  // bus and each circuit conductor's feeder are picked from it.
  sldElements?: SldElements | null | undefined;
};

// The earthing study of one arrangement at one bus (EOS-08a). The form owns the draft; a
// conductor row's figures start on a linked run of the active open revision, read by the
// backend when the study is saved. Every choice starts unchosen: the backend imposes no default.
export function EarthingForm({ disabled = false, onSubmit, sldElements }: EarthingFormProps) {
  const { activeRevisionId } = useProject();
  const revisionId = activeRevisionId();
  const { draft, setDraft, restored, clear, otherRevision, keepForThisRevision } =
    useStudyDraft<EarthingDraft>({
      module: EARTHING_DRAFT_MODULE,
      createInitial: createInitialEarthingDraft,
      schema: earthingDraftSchema,
      revisionId,
    });

  const [validationError, setValidationError] = useState<string | null>(null);
  const [rowToFocus, setRowToFocus] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    if (rowToFocus === null) {
      return;
    }
    formRef.current?.querySelector<HTMLInputElement>(`input[name="${rowToFocus}-code"]`)?.focus();
    setRowToFocus(null);
  }, [rowToFocus]);

  function updateStudy(patch: Partial<EarthingDraft>) {
    setDraft((current) => ({ ...current, ...patch }));
  }

  function updateConductor(id: string, patch: Partial<ConductorRowDraft>) {
    setDraft((current) => ({
      ...current,
      conductors: current.conductors.map((row) => (row.id === id ? { ...row, ...patch } : row)),
    }));
  }

  function updateElectrode(id: string, patch: Partial<ElectrodeDraft>) {
    setDraft((current) => ({
      ...current,
      electrodes: current.electrodes.map((item) => (item.id === id ? { ...item, ...patch } : item)),
    }));
  }

  // Stable for the selectors' effects that clear an unlisted run.
  const setFaultRunId = useCallback(
    (id: string, faultRunId: string) =>
      setDraft((current) => ({
        ...current,
        conductors: current.conductors.map((row) => (row.id === id ? { ...row, faultRunId } : row)),
      })),
    [setDraft],
  );
  const setDisconnectionLink = useCallback(
    (id: string, disconnectionRunId: string, disconnectionRowCode: string) =>
      setDraft((current) => ({
        ...current,
        conductors: current.conductors.map((row) =>
          row.id === id ? { ...row, disconnectionRunId, disconnectionRowCode } : row,
        ),
      })),
    [setDraft],
  );

  function addConductor() {
    seedEarthingIdCounters([...draft.conductors, ...draft.electrodes].map((item) => item.id));
    const row = createConductorRowDraft();
    setDraft((current) => ({ ...current, conductors: [...current.conductors, row] }));
    setRowToFocus(row.id);
  }

  function removeConductor(id: string) {
    setDraft((current) => ({ ...current, conductors: current.conductors.filter((row) => row.id !== id) }));
  }

  function addElectrode() {
    seedEarthingIdCounters([...draft.conductors, ...draft.electrodes].map((item) => item.id));
    const item = createElectrodeDraft();
    setDraft((current) => ({ ...current, electrodes: [...current.electrodes, item] }));
    setRowToFocus(item.id);
  }

  function removeElectrode(id: string) {
    setDraft((current) => ({ ...current, electrodes: current.electrodes.filter((item) => item.id !== id) }));
  }

  function clearForm() {
    setValidationError(null);
    clear();
  }

  // The selectors' own sentences stand alone, numbered by the row; every other issue is read
  // by its label.
  function describeIssue(issue: ValidationIssueLike): string {
    const last = issue.path.at(-1);
    if (last === "fault_run_id" || last === "disconnection_run_id" || last === "disconnection_row_code") {
      const index = issue.path[2];
      const prefix = typeof index === "number" ? `Conductor ${index + 1} — ` : "";
      if (last === "fault_run_id") {
        return `${prefix}${issue.code === "custom" ? issue.message : "Choose a fault run of this project revision."}`;
      }
      return `${prefix}Choose an earth-fault loop run of this project revision and one of its circuit rows.`;
    }
    return describeValidationIssue(issue, EARTHING_LABELS);
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setValidationError(null);

    const parsed = earthingRunCreateRequestSchema.safeParse(buildEarthingRunPayload(draft));

    if (!parsed.success) {
      const issue = parsed.error.issues[0];
      setValidationError(issue ? describeIssue(issue) : "Review the earthing inputs.");
      return;
    }

    await onSubmit(parsed.data);
  }

  const studyText = (
    label: string,
    field: keyof EarthingDraft,
    name: string,
    inputMode: "decimal" | "text" = "text",
  ) => (
    <label>
      {label}
      <input
        inputMode={inputMode}
        name={name}
        value={draft[field] as string}
        onChange={(event) => updateStudy({ [field]: event.target.value } as Partial<EarthingDraft>)}
      />
    </label>
  );

  // Field names carry the row's id ("earth-conductor-3-code"), stable across add and remove.
  const rowText = (
    row: ConductorRowDraft,
    label: string,
    field: keyof ConductorRowDraft,
    inputMode: "decimal" | "text" = "decimal",
  ) => (
    <label>
      {label}
      <input
        inputMode={inputMode}
        name={`${row.id}-${field}`}
        value={row[field]}
        onChange={(event) => updateConductor(row.id, { [field]: event.target.value })}
      />
    </label>
  );

  const rowChoice = <T extends string>(
    row: ConductorRowDraft,
    label: string,
    field: keyof ConductorRowDraft,
    options: readonly T[],
    labels: Record<T, string>,
  ) => (
    <label>
      {label}
      <select
        name={`${row.id}-${field}`}
        value={row[field]}
        onChange={(event) => updateConductor(row.id, { [field]: event.target.value })}
      >
        <option value="">{CHOOSE}</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {labels[option]}
          </option>
        ))}
      </select>
    </label>
  );

  const electrodeText = (
    item: ElectrodeDraft,
    label: string,
    field: keyof ElectrodeDraft,
    inputMode: "decimal" | "text" = "decimal",
  ) => (
    <label>
      {label}
      <input
        inputMode={inputMode}
        name={`${item.id}-${field}`}
        value={item[field]}
        onChange={(event) => updateElectrode(item.id, { [field]: event.target.value })}
      />
    </label>
  );

  const busCode = draft.busCode.trim();

  return (
    <form aria-label="Earthing inputs" ref={formRef} onSubmit={handleSubmit}>
      {restored ? <p role="status">Draft restored from this session.</p> : null}
      {otherRevision ? (
        <OtherRevisionDraftNotice onKeep={keepForThisRevision} onClear={clearForm} disabled={disabled} />
      ) : null}

      <fieldset disabled={disabled}>
        <legend>Study definition</legend>
        {studyText("Study code", "code", "studyCode")}
        {studyText("Study name", "name", "studyName")}
        <SldElementPicker
          label="Bus code"
          name="busCode"
          value={draft.busCode}
          onChange={(value) => updateStudy({ busCode: value })}
          categories={["bus"]}
          elements={sldElements}
        />
        <label>
          System earthing
          <select
            name="systemKind"
            value={draft.systemKind}
            onChange={(event) => updateStudy({ systemKind: event.target.value })}
          >
            <option value="">{CHOOSE}</option>
            {systemKindSchema.options.map((option) => (
              <option key={option} value={option}>
                {SYSTEM_KIND_LABELS[option]}
              </option>
            ))}
          </select>
        </label>
        {studyText("System voltage (V)", "systemVoltageV", "systemVoltageV", "decimal")}
        {studyText("System voltage source", "systemVoltageSource", "systemVoltageSource")}
        <label>
          Jurisdiction profile
          <select
            name="jurisdictionProfile"
            value={draft.jurisdictionProfile}
            onChange={(event) => updateStudy({ jurisdictionProfile: event.target.value })}
          >
            <option value="IN">India (CEA Regulations, IS, CPWD)</option>
            <option value="IEC">IEC (international, no national layer)</option>
            <option value="UK">United Kingdom (BS 7671) - reference data pending</option>
            <option value="EU">European Union (HD 60364) - reference data pending</option>
            <option value="US">United States (NEC / NFPA 70) - reference data pending</option>
            <option value="AU_NZ">Australia / New Zealand (AS/NZS 3000) - reference data pending</option>
          </select>
        </label>
        <p data-profile-note="true">{PROFILE_NOTE}</p>
        {studyText("Study notes", "notes", "studyNotes")}
      </fieldset>

      <fieldset disabled={disabled}>
        <legend>Protective conductors</legend>
        {draft.conductors.map((row, index) => {
          const number = index + 1;
          const board = isBoardRole(row.role);
          const circuit = row.role === "CIRCUIT_PROTECTIVE";
          return (
            <fieldset key={row.id} data-conductor-row={row.id}>
              <legend>Conductor {number}</legend>
              {rowText(row, "Conductor code", "code", "text")}
              {rowText(row, "Conductor name", "name", "text")}
              {rowChoice(row, "Role", "role", conductorRoleSchema.options, ROLE_LABELS)}
              <label>
                Figures from
                <select
                  name={`${row.id}-linkSource`}
                  value={row.linkSource}
                  onChange={(event) => updateConductor(row.id, { linkSource: event.target.value as LinkSourceChoice })}
                >
                  {LINK_SOURCES.map((source) => (
                    <option key={source} value={source}>
                      {LINK_SOURCE_LABELS[source]}
                    </option>
                  ))}
                </select>
              </label>
              {row.linkSource === "LINKED" && board ? (
                <FaultRunSelector
                  revisionId={revisionId}
                  faultRunId={row.faultRunId}
                  onChange={(value) => setFaultRunId(row.id, value)}
                  busCode={busCode}
                  withPeak={false}
                  notMaximumNote={NOT_MAXIMUM_EARTH_FAULT_NOTE}
                  busMismatchNoteFor={busMismatchNote}
                  isExpectedRun={isMaximumEarthFault}
                />
              ) : null}
              {row.linkSource === "LINKED" && circuit && revisionId !== null ? (
                <DisconnectionRowSelector
                  revisionId={revisionId}
                  name={row.id}
                  runId={row.disconnectionRunId}
                  rowCode={row.disconnectionRowCode}
                  onChange={(runId, rowCode) => setDisconnectionLink(row.id, runId, rowCode)}
                />
              ) : null}
              {rowText(row, "Fault current (A)", "faultCurrentA")}
              {rowText(row, "Fault current source", "faultCurrentSource", "text")}
              {rowText(row, "Let-through energy I²t (A²s)", "letThroughEnergyA2s")}
              {rowText(row, "Let-through energy source", "letThroughEnergySource", "text")}
              {rowText(row, "Disconnection time (s)", "disconnectionTimeS")}
              {rowText(row, "Disconnection time source", "disconnectionTimeSource", "text")}
              {rowText(row, "Conductor area (mm²)", "areaMm2")}
              {rowText(row, "Conductor area source", "areaSource", "text")}
              {rowChoice(row, "Material", "conductorMaterial", conductorMaterialSchema.options, MATERIAL_LABELS)}
              <label>
                k from
                <select
                  name={`${row.id}-kSource`}
                  value={row.kSource}
                  onChange={(event) => updateConductor(row.id, { kSource: event.target.value as KSourceChoice })}
                >
                  {K_SOURCES.map((source) => (
                    <option key={source} value={source}>
                      {K_SOURCE_LABELS[source]}
                    </option>
                  ))}
                </select>
              </label>
              {row.kSource === "TABLE" ? (
                <>
                  <label>
                    k table
                    <select
                      name={`${row.id}-kTable`}
                      value={row.kTable}
                      onChange={(event) => updateConductor(row.id, { kTable: event.target.value, kColumn: "" })}
                    >
                      <option value="">{CHOOSE}</option>
                      {kTableIdSchema.options.map((option) => (
                        <option key={option} value={option}>
                          {K_TABLE_LABELS[option]}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    k table column
                    <select
                      name={`${row.id}-kColumn`}
                      value={row.kColumn}
                      onChange={(event) => updateConductor(row.id, { kColumn: event.target.value })}
                    >
                      <option value="">{CHOOSE}</option>
                      {columnsOf(row.kTable).map((column) => (
                        <option key={column} value={column}>
                          {K_COLUMN_LABELS[column] ?? column}
                        </option>
                      ))}
                    </select>
                  </label>
                </>
              ) : null}
              {row.kSource === "DECLARED" ? (
                <>
                  {rowText(row, "k (A·√s/mm²)", "kValue")}
                  {rowText(row, "k source", "kValueSource", "text")}
                </>
              ) : null}
              {circuit ? (
                <SldElementPicker
                  label="Feeder (SLD)"
                  name={`${row.id}-sldElementCode`}
                  value={row.sldElementCode}
                  onChange={(value) => updateConductor(row.id, { sldElementCode: value })}
                  categories={["feeder"]}
                  elements={sldElements}
                />
              ) : null}
              {rowText(row, "Conductor notes", "notes", "text")}
              {draft.conductors.length > 0 ? (
                <button type="button" onClick={() => removeConductor(row.id)}>
                  Remove conductor {number}
                </button>
              ) : null}
            </fieldset>
          );
        })}
        <button type="button" onClick={addConductor}>
          Add conductor
        </button>
        <p data-conductor-hint="true">{CONDUCTOR_HINT}</p>
        <p data-k-hint="true">{K_HINT}</p>
      </fieldset>

      <fieldset disabled={disabled}>
        <legend>Earth electrodes</legend>
        {draft.electrodes.map((item, index) => {
          const number = index + 1;
          const dimensions: readonly string[] =
            item.kind in ELECTRODE_DIMENSIONS ? ELECTRODE_DIMENSIONS[item.kind as keyof typeof ELECTRODE_DIMENSIONS] : [];
          return (
            <fieldset key={item.id} data-electrode-row={item.id}>
              <legend>Electrode {number}</legend>
              {electrodeText(item, "Electrode code", "code", "text")}
              <label>
                Electrode kind
                <select
                  name={`${item.id}-kind`}
                  value={item.kind}
                  onChange={(event) => updateElectrode(item.id, { kind: event.target.value })}
                >
                  <option value="">{CHOOSE}</option>
                  {electrodeKindSchema.options.map((option) => (
                    <option key={option} value={option}>
                      {ELECTRODE_KIND_LABELS[option]}
                    </option>
                  ))}
                </select>
              </label>
              {dimensions.map((name) => {
                const field = ELECTRODE_DIMENSION_FIELDS[name];
                return field === undefined ? null : (
                  <span key={name}>{electrodeText(item, EARTHING_LABELS.fields[`study.electrodes.*.${name}`] ?? name, field)}</span>
                );
              })}
              {electrodeText(item, "Dimension source", "dimensionSource", "text")}
              {electrodeText(item, "Electrode material", "material", "text")}
              {electrodeText(item, "Number in parallel", "countInParallel")}
              {electrodeText(item, "Utilisation factor", "parallelFactor")}
              {electrodeText(item, "Utilisation factor source", "parallelFactorSource", "text")}
              {electrodeText(item, "Spacing (m)", "spacingM")}
              {electrodeText(item, "Electrode notes", "notes", "text")}
              <button type="button" onClick={() => removeElectrode(item.id)}>
                Remove electrode {number}
              </button>
            </fieldset>
          );
        })}
        <button type="button" onClick={addElectrode}>
          Add electrode
        </button>
        <p data-electrode-hint="true">{ELECTRODE_HINT}</p>
      </fieldset>

      <fieldset disabled={disabled}>
        <legend>Soil</legend>
        {studyText("Soil resistivity (Ω·m)", "soilResistivityOhmM", "soilResistivityOhmM", "decimal")}
        {studyText("Test method", "soilTestMethod", "soilTestMethod")}
        {studyText("Test date", "soilTestDate", "soilTestDate")}
        {studyText("Test report", "soilSource", "soilSource")}
        {studyText("Seasonal factor", "seasonalFactor", "seasonalFactor", "decimal")}
        {studyText("Seasonal factor source", "seasonalFactorSource", "seasonalFactorSource")}
        <p data-soil-hint="true">{SOIL_HINT}</p>
      </fieldset>

      <fieldset disabled={disabled}>
        <legend>Target resistance</legend>
        {studyText("Target resistance (Ω)", "targetResistanceOhm", "targetResistanceOhm", "decimal")}
        {studyText("Target resistance source", "targetResistanceSource", "targetResistanceSource")}
      </fieldset>

      {validationError ? <p role="alert">{validationError}</p> : null}

      <button disabled={disabled} type="submit">
        Check earthing
      </button>
      <button disabled={disabled} type="button" onClick={clearForm}>
        Clear form
      </button>
    </form>
  );
}

export default EarthingForm;
